import React, { useState, useCallback, useEffect, useRef } from "react";
import { css } from "@emotion/react";
import Checkbox from "molecules/Checkbox";
import CheckboxGroup from "molecules/CheckboxGroup";
import { DropDownProps } from "./types";
import Arrow from "icons/Arrow";
import Flexbox from "atoms/Flexbox";
import { ValueType } from "atoms/types";
import RadioGroup from "molecules/RadioGroup";
import Radio from "molecules/Radio";

// Styles
const dropdownContainerStyle = css`
	position: relative;
	display: inline-block;
	width: 100%;
`;

const dropdownStyle = css`
	background-color: var(--color-dark-gray-neutral);
	color: var(--color-gray-neutral);
	padding: var(--spacing-md);
	border: solid 1px var(--dark-gray);
	cursor: pointer;
	border-radius: var(--spacing-sm);
`;

// Base text styling for layout
const baseTextStyle = css`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	padding-right: 24px;
`;

// When some value is selected,
const selectedTextStyle = css`
	${baseTextStyle};
	color: white;
`;

// When no value is selected (i.e. display "Select"),
const placeholderTextStyle = css`
	${baseTextStyle};
	color: var(--color-gray-neutral);
`;

const iconStyle = css`
	position: absolute;
	top: 50%;
	right: var(--spacing-md);
	transform: translateY(-50%);
`;

const dropdownContentStyle = (isOpen: boolean) => css`
	display: ${isOpen ? "block" : "none"};
	position: absolute;
	padding: var(--spacing-lg);
	width: 100%;
	border-radius: var(--spacing-xxl);
	background-color: var(--color-dark-gray-neutral);
	z-index: 1;
	overflow-y: auto;
	border: 2px solid var(--color-dark-gray-neutral);
`;

const DropDown: React.FC<DropDownProps> = ({
	data,
	onChange,
	variant = "single-select",
	disabledItemsValues,
	preSelectedValues
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [selectedValues, setSelectedValues] = useState<ValueType[]>(
		preSelectedValues || []
	);
	const dropdownRef = useRef<HTMLDivElement>(null);

	// Toggle dropdown state
	const toggleDropdown = useCallback(() => {
		setIsOpen(prev => !prev);
	}, []);

	// Handle checkbox changes (multi-select or single-select)
	const handleCheckboxChange = useCallback(
		(values: string[]) => {
			if (variant === "multi-select") {
				setSelectedValues(values);
				onChange(values);
			} else {
				const newSelected = values.length > 0 ? values.slice(-1) : [];
				setSelectedValues(newSelected);
				onChange(newSelected);
				setIsOpen(false); // Close dropdown for single-select
			}
		},
		[variant, onChange]
	);

	// Handle checkbox changes (multi-select or single-select)
	const handleRadioBtnChange = useCallback(
		(values: string) => {
			setSelectedValues([values]);
			onChange([values]);
			setIsOpen(false); // Close dropdown for single-select
		},
		[variant, onChange]
	);

	const displayText =
		selectedValues.length > 0 ? selectedValues.join(", ") : "Select";

	// Close the dropdown when clicked outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setIsOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	return (
		<div css={dropdownContainerStyle} ref={dropdownRef}>
			<div css={dropdownStyle} onClick={toggleDropdown}>
				<div
					css={
						selectedValues.length > 0 ? selectedTextStyle : placeholderTextStyle
					}
				>
					{displayText}
				</div>
				<div css={iconStyle}>
					{isOpen ? (
						<Arrow direction="up" fillPath="var(--color-gray-neutral)" />
					) : (
						<Arrow direction="down" fillPath="var(--color-gray-neutral)" />
					)}
				</div>
			</div>
			<Flexbox
				direction="row"
				css={dropdownContentStyle(isOpen)}
				justify="center"
				align="center"
				mt="sm"
			>
				{variant === "single-select" ? (
					<RadioGroup value={selectedValues[0]} onChange={handleRadioBtnChange}>
						{data.map(option => (
							<Flexbox
								mb="lg"
								direction="row"
								justify="flex-start"
								align="center"
							>
								<Radio
									key={option.value}
									value={option.value}
									checked={selectedValues.includes(option.value)}
									disabled={(disabledItemsValues || []).includes(option.value)}
								>
									{option.label}
								</Radio>
							</Flexbox>
						))}
					</RadioGroup>
				) : (
					<CheckboxGroup value={selectedValues} onChange={handleCheckboxChange}>
						{data.map(option => (
							<Flexbox
								mb="lg"
								direction="row"
								justify="flex-start"
								align="center"
							>
								<Checkbox
									key={option.value}
									value={option.value}
									checked={selectedValues.includes(option.value)}
									disabled={(disabledItemsValues || []).includes(option.value)}
								>
									{option.label}
								</Checkbox>
							</Flexbox>
						))}
					</CheckboxGroup>
				)}
			</Flexbox>
		</div>
	);
};

export default DropDown;
